/**
 * UserNotesSidePaper Component
 * 
 * A sticky side panel that allows managers to view and manage user notes
 * while viewing other user information like tickets or chat.
 * 
 * Features:
 * - Sticky positioning on the right side of the screen
 * - Minimizable to a floating action button
 * - Collapsible content area
 * - Full CRUD operations for user notes
 * - Scrollable notes list
 * - Real-time updates
 * 
 * Usage:
 * ```tsx
 * import UserNotesSidePaper from './components/UserNotesSidePaper/UserNotesSidePaper';
 * 
 * const MyComponent = () => {
 *   return (
 *     <div>
 *       <main>Main content here</main>
 *       <UserNotesSidePaper User_guid="user-guid-here" />
 *     </div>
 *   );
 * };
 * ```
 * 
 * Props:
 * - User_guid: string - The UUID of the user whose notes to display/manage
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Fab,
  Chip,
  MenuItem,
  Stack,
  Link
} from '@mui/material';
import NoteIcon from '@mui/icons-material/Note';
import AddIcon from '@mui/icons-material/Add';
import PhoneIcon from '@mui/icons-material/Phone';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MinimizeIcon from '@mui/icons-material/Minimize';

import './UserNotesSidePaper.scss';

interface UserNoteDto {
  UserNote_guid: string;
  UserNote_Title: string;
  UserNote_Desc: string;
  User_id: number;
  Ticket_guid: string | null;
  UserNote_AuthorName?: string | null;
  UserNote_CreatedAt?: string | Date | null;
  UserNote_Kind?: CallKind;
  UserNote_CallDirection?: CallDirection | null;
  UserNote_CallOutcome?: CallOutcome | null;
  UserNote_Source?: CallSource | null;
  UserNote_CallDurationSeconds?: number | null;
  UserNote_CallRecordingUrl?: string | null;
  UserNote_CallStartedAt?: string | Date | null;
}

type CallKind = 'note' | 'call';
type CallDirection = 'outbound' | 'inbound';
type CallOutcome = 'spoke' | 'voicemail' | 'no-answer';
// 'manual' is a call a person logged themselves; 'dialpad' arrived automatically off a webhook.
type CallSource = 'manual' | 'dialpad';

const CALL_DIRECTIONS: Array<{ value: CallDirection; label: string }> = [
  { value: 'outbound', label: 'We called them' },
  { value: 'inbound', label: 'They called us' },
];

const CALL_OUTCOMES: Array<{ value: CallOutcome; label: string }> = [
  { value: 'spoke', label: 'Spoke with them' },
  { value: 'voicemail', label: 'Left / received voicemail' },
  { value: 'no-answer', label: 'No answer' },
];

/**
 * The one-line summary that becomes the note title. Written out in full rather than assembled from
 * the raw values, because this is what someone reads back months later when a client says nobody
 * ever rang them — "Outbound call - no answer" has to stand on its own.
 */
function describeCall(direction: CallDirection, outcome: CallOutcome): string {
  if (direction === 'outbound') {
    if (outcome === 'spoke') return 'Outbound call - spoke with client';
    if (outcome === 'voicemail') return 'Outbound call - left voicemail';
    return 'Outbound call - no answer';
  }
  if (outcome === 'spoke') return 'Inbound call - spoke with client';
  if (outcome === 'voicemail') return 'Inbound call - voicemail received';
  return 'Inbound call - missed';
}

/**
 * Automatic call notes carry a start time distinct from UserNote_CreatedAt — CreatedAt is stamped
 * when the Dialpad webhook lands, which trails the actual call by however long processing took.
 * Prefer the call's own start time for display; fall back to CreatedAt when it's missing.
 */
function getCallDisplayTimestamp(note: UserNoteDto): string | Date | null | undefined {
  if (note.UserNote_Kind === 'call' && note.UserNote_Source === 'dialpad') {
    return note.UserNote_CallStartedAt || note.UserNote_CreatedAt;
  }
  return note.UserNote_CreatedAt;
}

/** Formats a call duration in seconds as "Xm Ys" (or "Ys" alone under a minute). */
function formatCallDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

interface UserNotesSidePaperProps {
  User_guid: string;
  Ticket_guid?: string;
  /**
   * Where the panel is drawn (Lloyd, Aug 19).
   *
   * 'floating' is the original behaviour: a fixed panel pinned bottom-right, which opened over the
   * case the moment you clicked into it and covered roughly a third of the screen. 'inline' drops
   * the same notes into ordinary page flow so they can live in the case tab strip beside Status
   * History and the rest, where they take exactly the room the tab gives them and nothing more.
   */
  variant?: 'floating' | 'inline';
}

const UserNotesSidePaper: React.FC<UserNotesSidePaperProps> = ({
  User_guid,
  Ticket_guid,
  variant = 'floating',
}) => {
  const isInline = variant === 'inline';
  const [userNotes, setUserNotes] = useState<UserNoteDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Panel state. The floating panel starts minimized — opening a case should show the case, not a
  // notes window on top of it.
  const [isMinimized, setIsMinimized] = useState(true);
  
  // Note dialog state
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<UserNoteDto | null>(null);
  const [noteFormData, setNoteFormData] = useState({
    title: '',
    description: ''
  });
  const [noteSubmitting, setNoteSubmitting] = useState(false);

  // Call logging (Lloyd, Jul 26). Defaults to the common case — we rang them and got through — so
  // the usual log is open, confirm, done.
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [callSubmitting, setCallSubmitting] = useState(false);
  const [callForm, setCallForm] = useState<{
    direction: CallDirection;
    outcome: CallOutcome;
    description: string;
  }>({ direction: 'outbound', outcome: 'spoke', description: '' });

  // Ref for scroll container
  const contentRef = useRef<HTMLDivElement>(null);

  // Fetch user notes
  const fetchUserNotes = async () => {
    if (!User_guid) return;
    
    setLoading(true);
    setError(null);

    try {
      let url = `${process.env.API_HOST || ''}/api/v1/user-note/list?User_guid=${User_guid}`;
      
      // Add Ticket_guid filter if provided
      if (Ticket_guid) {
        url += `&Ticket_guid=${Ticket_guid}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user notes: ${response.status} ${response.statusText}`);
      }

      const data: UserNoteDto[] = await response.json();
      setUserNotes(data);
      
    } catch (err) {
      console.error('Error fetching user notes:', err);
      setError(err instanceof Error ? err.message : 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  // Create or update note
  const handleSaveNote = async () => {
    if (!User_guid || !noteFormData.title.trim() || !noteFormData.description.trim()) {
      setError('Please fill in both title and description');
      return;
    }

    setNoteSubmitting(true);
    setError(null);

    try {
      if (editingNote) {
        // Update existing note
        const response = await fetch(
          `${process.env.API_HOST || ''}/api/v1/user-note`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              UserNote_guid: editingNote.UserNote_guid,
              UserNote_Title: noteFormData.title,
              UserNote_Desc: noteFormData.description,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to update note: ${response.status} ${response.statusText}`);
        }

        setSuccess('Note updated successfully!');
      } else {
        // Create new note
        const requestBody: any = {
          User_guid: User_guid,
          UserNote_Title: noteFormData.title,
          UserNote_Desc: noteFormData.description,
        };

        // Add Ticket_guid if provided
        if (Ticket_guid) {
          requestBody.Ticket_guid = Ticket_guid;
        }

        const response = await fetch(
          `${process.env.API_HOST || ''}/api/v1/user-note`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to create note: ${response.status} ${response.statusText}`);
        }

        setSuccess('Note created successfully!');
      }

      setNoteDialogOpen(false);
      fetchUserNotes(); // Refresh the notes list
    } catch (err) {
      console.error('Error saving note:', err);
      setError(err instanceof Error ? err.message : 'Failed to save note');
    } finally {
      setNoteSubmitting(false);
    }
  };

  // Log a call. Writes an ordinary note carrying UserNote_Kind 'call', so it appears in the same
  // history as everything else — the server stamps who logged it and when.
  const handleSaveCall = async () => {
    setCallSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const summary = describeCall(callForm.direction, callForm.outcome);
      const requestBody: Record<string, unknown> = {
        User_guid,
        UserNote_Title: summary,
        // Desc is required by the API, so fall back to the summary when nothing was typed.
        UserNote_Desc: callForm.description.trim() || summary,
        UserNote_Kind: 'call',
        UserNote_CallDirection: callForm.direction,
        UserNote_CallOutcome: callForm.outcome,
      };
      if (Ticket_guid) {
        requestBody.Ticket_guid = Ticket_guid;
      }

      const response = await fetch(`${process.env.API_HOST || ''}/api/v1/user-note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`Failed to log call: ${response.status} ${response.statusText}`);
      }

      setSuccess('Call logged.');
      setCallDialogOpen(false);
      setCallForm({ direction: 'outbound', outcome: 'spoke', description: '' });
      fetchUserNotes();
    } catch (err) {
      console.error('Error logging call:', err);
      setError(err instanceof Error ? err.message : 'Failed to log call');
    } finally {
      setCallSubmitting(false);
    }
  };

  /**
   * Move a client-level note onto the case being read (Kimberly, Aug 20 — "I'm missing how to attach
   * a note to a specific case").
   *
   * The client page now asks which case a new note is about, but that does nothing for the notes
   * already written before it did, and every one of those is sitting unfiled. Retyping them onto the
   * case and deleting the originals was the only route, which loses the author and the date. This
   * files the note where it is being read, from the screen where the mistake is visible.
   *
   * Only offered when this panel is showing a specific case — on the client page there is no case to
   * file to, and the dropdown there covers it.
   */
  const [filingNoteGuid, setFilingNoteGuid] = useState<string | null>(null);

  const handleFileNoteToCase = async (noteGuid: string) => {
    if (!Ticket_guid) return;

    setFilingNoteGuid(noteGuid);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${process.env.API_HOST || ''}/api/v1/user-note`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ UserNote_guid: noteGuid, Ticket_guid }),
      });

      if (!response.ok) {
        throw new Error(`Failed to file note: ${response.status} ${response.statusText}`);
      }

      setSuccess('Note filed to this case.');
      fetchUserNotes();
    } catch (err) {
      console.error('Error filing note to case:', err);
      setError(err instanceof Error ? err.message : 'Failed to file note to this case');
    } finally {
      setFilingNoteGuid(null);
    }
  };

  // Delete note
  const handleDeleteNote = async (noteGuid: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/user-note?UserNote_guid=${noteGuid}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete note: ${response.status} ${response.statusText}`);
      }

      setSuccess('Note deleted successfully!');
      fetchUserNotes(); // Refresh the notes list
    } catch (err) {
      console.error('Error deleting note:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete note');
    }
  };

  // Dialog handlers
  const handleCreateNote = () => {
    setEditingNote(null);
    setNoteFormData({ title: '', description: '' });
    setNoteDialogOpen(true);
  };

  const handleEditNote = (note: UserNoteDto) => {
    setEditingNote(note);
    setNoteFormData({ title: note.UserNote_Title, description: note.UserNote_Desc });
    setNoteDialogOpen(true);
  };

  const handleNoteDialogClose = () => {
    setNoteDialogOpen(false);
    setEditingNote(null);
    setNoteFormData({ title: '', description: '' });
    setError(null);
  };

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Fetch notes on mount and when User_guid or Ticket_guid changes
  useEffect(() => {
    if (User_guid) {
      fetchUserNotes();
    }
  }, [User_guid, Ticket_guid]);

  // Auto-scroll to bottom when notes change (to show newest notes)
  useEffect(() => {
    const contentElement = contentRef.current;
    if (contentElement && userNotes.length > 0) {
      setTimeout(() => {
        contentElement.scrollTop = contentElement.scrollHeight;
      }, 100);
    }
  }, [userNotes]);

  // Prevent scroll propagation to page when component reaches scroll limits
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleWheel = (e: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = contentElement;
      const isScrollingUp = e.deltaY < 0;
      const isScrollingDown = e.deltaY > 0;
      
      // Prevent page scroll when at scroll boundaries
      if (
        (isScrollingUp && scrollTop === 0) ||
        (isScrollingDown && scrollTop + clientHeight >= scrollHeight)
      ) {
        e.preventDefault();
      }
    };

    contentElement.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      contentElement.removeEventListener('wheel', handleWheel);
    };
  }, []);

  if (!isInline && isMinimized) {
    return (
      <Fab
        className="user-notes-fab"
        color="primary"
        aria-label="user notes"
        onClick={() => setIsMinimized(false)}
        sx={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          zIndex: 1300
        }}
      >
        <NoteIcon />
      </Fab>
    );
  }

  return (
    <>
      <Paper
        className={isInline ? 'user-notes-inline' : 'user-notes-side-paper'}
        elevation={isInline ? 0 : 8}
      >
        {/* Header */}
        <Box className="user-notes-header">
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <NoteIcon sx={{ mr: 1 }} />
            <Typography variant="h6" component="h2">
              {isInline ? 'Notes' : 'User Notes'} ({userNotes.length})
            </Typography>
          </Box>
          {!isInline && (
            <IconButton
              size="small"
              onClick={() => setIsMinimized(true)}
              aria-label="minimize"
            >
              <MinimizeIcon />
            </IconButton>
          )}
        </Box>

        <Divider />

        {/* Content */}
        <Box ref={contentRef} className="user-notes-content">
            {/* Log a call sits beside Add Note: same history, one click for the common case. */}
            <Button
              variant="outlined"
              startIcon={<PhoneIcon />}
              onClick={() => setCallDialogOpen(true)}
              fullWidth
              sx={{ mt: 2 }}
            >
              Log a Call
            </Button>

            {/* Add Note Button */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNote}
              fullWidth
              sx={{ mt: 1 }}
            >
              Add Note
            </Button>

            {/* Messages */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            {/* Notes List */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : userNotes.length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', p: 2 }}>
                No notes found for this user.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {userNotes.map((note) => {
                  const isAutomaticCall = note.UserNote_Kind === 'call' && note.UserNote_Source === 'dialpad';
                  const displayTimestamp = getCallDisplayTimestamp(note);
                  return (
                  <Card key={note.UserNote_guid} variant="outlined">
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                          {note.UserNote_Title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditNote(note)}
                            sx={{ p: 0.5 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteNote(note.UserNote_guid)}
                            sx={{ p: 0.5 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', lineHeight: 1.4 }}>
                        {note.UserNote_Desc}
                      </Typography>
                      {/*
                        The panel now also shows the client's unfiled notes and notes written on a
                        duplicate copy of this case, so that nothing hides. Say which is which rather
                        than quietly presenting another record's note as this one's.
                      */}
                      {note.UserNote_Kind === 'call' && (
                        <Chip
                          icon={<PhoneIcon sx={{ fontSize: '0.7rem' }} />}
                          label="Logged call"
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ mt: 0.75, mr: 0.5, height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                      {isAutomaticCall && (
                        <Chip
                          label="Automatic — Dialpad"
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ mt: 0.75, mr: 0.5, height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                      {isAutomaticCall && (note.UserNote_CallDurationSeconds != null || note.UserNote_CallRecordingUrl) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                          {note.UserNote_CallDurationSeconds != null && (
                            <Typography variant="caption" color="text.secondary">
                              Duration: {formatCallDuration(note.UserNote_CallDurationSeconds)}
                            </Typography>
                          )}
                          {note.UserNote_CallRecordingUrl && (
                            <Link
                              href={note.UserNote_CallRecordingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="caption"
                              underline="hover"
                            >
                              Open in Dialpad
                            </Link>
                          )}
                        </Box>
                      )}
                      {!note.Ticket_guid && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                          <Chip
                            label="Client note — not filed to a case"
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                          {/*
                            Offered only on a case page, and only for a hand-typed note: an automatic
                            call record is already filed by the webhook, and a note read on the client
                            page has no case to file to.
                          */}
                          {Ticket_guid && note.UserNote_Source !== 'dialpad' && (
                            <Button
                              size="small"
                              variant="text"
                              disabled={filingNoteGuid === note.UserNote_guid}
                              onClick={() => handleFileNoteToCase(note.UserNote_guid)}
                              sx={{ minWidth: 0, p: 0, fontSize: '0.65rem', lineHeight: 1.2 }}
                            >
                              {filingNoteGuid === note.UserNote_guid ? 'Filing…' : 'File to this case'}
                            </Button>
                          )}
                        </Box>
                      )}
                      {note.Ticket_guid && Ticket_guid && note.Ticket_guid !== Ticket_guid && (
                        <Chip
                          label="From a duplicate copy of this case"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ mt: 0.75, height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                      {(note.UserNote_AuthorName || displayTimestamp) && (
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                          {note.UserNote_AuthorName || 'Staff'}
                          {displayTimestamp ? ` · ${new Date(displayTimestamp).toLocaleString('en-US')}` : ''}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </Box>
            )}
        </Box>
      </Paper>

      {/* Note Dialog */}
      <Dialog 
        open={noteDialogOpen} 
        onClose={handleNoteDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingNote ? 'Edit Note' : 'Create New Note'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Note Title"
            fullWidth
            variant="outlined"
            value={noteFormData.title}
            onChange={(e) => setNoteFormData({ ...noteFormData, title: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Note Description"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={noteFormData.description}
            onChange={(e) => setNoteFormData({ ...noteFormData, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleNoteDialogClose}
            disabled={noteSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveNote}
            variant="contained"
            disabled={noteSubmitting || !noteFormData.title.trim() || !noteFormData.description.trim()}
          >
            {noteSubmitting ? 'Saving...' : (editingNote ? 'Update Note' : 'Create Note')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Log a Call dialog */}
      <Dialog
        open={callDialogOpen}
        onClose={() => !callSubmitting && setCallDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Log a Call</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The date, time and your name are recorded automatically.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Direction"
              value={callForm.direction}
              onChange={(e) =>
                setCallForm({ ...callForm, direction: e.target.value as CallDirection })
              }
              fullWidth
            >
              {CALL_DIRECTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="What happened"
              value={callForm.outcome}
              onChange={(e) =>
                setCallForm({ ...callForm, outcome: e.target.value as CallOutcome })
              }
              fullWidth
            >
              {CALL_OUTCOMES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Notes (optional)"
              value={callForm.description}
              onChange={(e) => setCallForm({ ...callForm, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="What was discussed, anything to follow up"
            />
            <Typography variant="caption" color="text.secondary">
              Will be saved as: {describeCall(callForm.direction, callForm.outcome)}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCallDialogOpen(false)} disabled={callSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSaveCall} variant="contained" disabled={callSubmitting}>
            {callSubmitting ? 'Saving...' : 'Log Call'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserNotesSidePaper;